import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Eye, Menu, X, Download } from 'lucide-react';
import AdminLoginModal from './adminLoginModal';

interface NavItemProps {
  number: string;
  stretchedText: string;
  normalText: string;
  to: string;
  onClick?: () => void;
}

const NavItem = ({ number, stretchedText, normalText, to, onClick }: NavItemProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = location.pathname === to;

  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth < 768);
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const handleClick = (e: React.MouseEvent) => {
    if (isMobile && onClick) {
      e.preventDefault(); // Prevent immediate navigation
      
      // Allow the link transition animation to play first (300ms)
      setTimeout(() => {
        onClick(); // Close mobile menu with animation
        
        // Add additional delay before navigation on mobile
        setTimeout(() => {
          navigate(to);
        }, 300);
      }, 300);
    } else if (onClick) {
      onClick(); // Just close menu on desktop (no delay needed)
    }
    // For desktop without onClick, Link handles navigation normally
  };
  
  return (
    <Link 
      to={to}
      onClick={handleClick}
      className={`flex gap-3 cursor-pointer ${isActive ? 'text-blue-400' : 'text-white'}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <span className='text-sm mt-auto'>{number}</span>
      <div className='relative text-2xl md:text-5xl font-stretchRegular overflow-hidden'>
        <span 
          className={`block transition-transform duration-300 ease-in-out ${
            isHovered || isActive ? 'transform -translate-y-full' : 'transform translate-y-0'
          }`}
        >
          {stretchedText}
        </span>
        <span 
          className={`absolute top-0 left-0 w-full transition-transform duration-300 ease-in-out ${
            isHovered || isActive ? 'transform translate-y-0' : 'transform translate-y-full'
          }`}
        >
          {normalText}
        </span>
      </div>
    </Link>
  );
};

const Sidebar: React.FC = () => {
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const handleAdminClick = () => {
    setIsAdminModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsAdminModalOpen(false);
  };

  const openMobileMenu = () => {
    setIsMobileMenuOpen(true);
    setTimeout(() => {
      setIsAnimating(true);
    }, 10);
  };

  const closeMobileMenu = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsMobileMenuOpen(false);
    }, 300);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    
    if (isLeftSwipe) {
      closeMobileMenu();
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMobileMenuOpen) {
        closeMobileMenu();
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const handleResumeDownload = () => {
    const link = document.createElement('a')
    link.href = '/Resume.pdf'
    link.download = 'Ahmed_Elghamriny_Resume.pdf'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <>
      {/* Mobile Menu Button - Fixed position with safe area */}
      <button
        onClick={() => isMobileMenuOpen ? closeMobileMenu() : openMobileMenu()}
        className="md:hidden fixed z-[60] p-3 bg-black/50 backdrop-blur-sm rounded-lg text-white transition-all duration-300 hover:scale-105 hover:bg-black/70"
        style={{
          top: 'max(1.5rem, env(safe-area-inset-top))',
          left: 'max(1.5rem, env(safe-area-inset-left))'
        }}
        aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
      >
        <div className="relative w-6 h-6">
          <Menu className={`w-6 h-6 absolute transition-all duration-300 ease-in-out ${
            isMobileMenuOpen ? 'rotate-45 opacity-0 scale-75' : 'rotate-0 opacity-100 scale-100'
          }`} />
          <X className={`w-6 h-6 absolute transition-all duration-300 ease-in-out ${
            isMobileMenuOpen ? 'rotate-0 opacity-100 scale-100' : '-rotate-45 opacity-0 scale-75'
          }`} />
        </div>
      </button>

      {/* Desktop Sidebar */}
      <div className='hidden md:flex absolute inset-0 text-white flex-col justify-between w-1/3 border-r border-white/20 p-8'>
        <div className='flex flex-col gap-2 border-b border-white/20 pb-4'>
          <p className='text-5xl font-stretchRegular'>Ahmed Elghamriny</p>
          <p className='text-sm'>Full Stack Developer & AI engineer</p>
        </div>
        
        <div className='flex flex-col gap-7 w-fit'>
          <NavItem 
            number="01" 
            stretchedText="HOMEE" 
            normalText="HOME"
            to="/"
          />
          <NavItem 
            number="02" 
            stretchedText="PORRTFOLIO" 
            normalText="PORTFOLIO"
            to="/portfolio"
          />
          <NavItem 
            number="03" 
            stretchedText="ABBOUT" 
            normalText="ABOUT"
            to="/about"
          />
          <NavItem 
            number="04" 
            stretchedText="COONTACT" 
            normalText="CONTACT"
            to="/contact"
          />
          <NavItem 
            number="05" 
            stretchedText="REESUME" 
            normalText="RESUME"
            to=''
            onClick={handleResumeDownload}
          />
        </div>
        
        <div className='text-sm flex justify-between border-t border-white/20 pt-4'>
          <div className=''>
            Building Scalable Software.<br/>
            Engineering Intelligent Systems.
          </div>
          <div className='mt-auto'>
            <button
              onClick={handleAdminClick}
              className="group p-2 rounded-lg hover:bg-white/5 transition-colors"
              title="Admin Access"
            >
              <Eye className='w-6 h-6 cursor-pointer'/>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <>
          <div 
            className={`md:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-[45] transition-opacity duration-300 ${
              isAnimating ? 'opacity-100' : 'opacity-0'
            }`}
            onClick={closeMobileMenu}
          />
          
          {/* Mobile Menu - Improved layout with proper spacing */}
          <div 
            className={`md:hidden fixed left-0 w-full max-w-sm bg-gradient-to-br from-orange-500 via-pink-500 to-purple-600 z-50 flex flex-col transform transition-transform duration-300 ease-in-out overflow-y-auto ${
              isAnimating ? 'translate-x-0' : '-translate-x-full'
            }`}
            style={{
              top: '0',
              bottom: '0',
              height: '100vh',
              minHeight: '-webkit-fill-available'
            }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          > 
            {/* Scrollable content wrapper */}
            <div className="flex flex-col min-h-full px-6 sm:px-8 py-6">
              {/* Top spacer for menu button */}
              <div className="h-16 flex-shrink-0" />
              
              {/* Name and title */}
              <div className='flex flex-col gap-2 border-b border-white/20 pb-4 mb-6 flex-shrink-0'>
                <p className='text-2xl sm:text-3xl font-stretchRegular text-white leading-tight'>Ahmed Elghamriny</p>
                <p className='text-xs sm:text-sm text-white/80'>Full Stack Developer & AI engineer</p>
              </div>
              
              {/* Navigation - centered in remaining space */}
              <div className='flex-1 flex items-center py-4'>
                <div className='flex flex-col gap-5 sm:gap-6 w-full'>
                  <NavItem 
                    number="01" 
                    stretchedText="HOMEE" 
                    normalText="HOME"
                    to="/"
                    onClick={closeMobileMenu}
                  />
                  <NavItem 
                    number="02" 
                    stretchedText="PORRTFOLIO" 
                    normalText="PORTFOLIO"
                    to="/portfolio"
                    onClick={closeMobileMenu}
                  />
                  <NavItem 
                    number="03" 
                    stretchedText="ABBOUT" 
                    normalText="ABOUT"
                    to="/about"
                    onClick={closeMobileMenu}
                  />
                  <NavItem 
                    number="04" 
                    stretchedText="COONTACT" 
                    normalText="CONTACT"
                    to="/contact"
                    onClick={closeMobileMenu}
                  />
                  <NavItem 
                    number="05" 
                    stretchedText="REESUME" 
                    normalText="RESUME"
                    to=''
                    onClick={handleResumeDownload}
                  />
                </div>
              </div>
              
              {/* Footer */}
              <div className='text-xs sm:text-sm flex justify-between border-t border-white/20 pt-4 text-white flex-shrink-0 mt-6 mb-safe'>
                <div className='leading-relaxed'>
                  Building Scalable Software.<br/>
                  Engineering Intelligent Systems.
                </div>
                <div className='mt-auto flex-shrink-0'>
                  <button
                    onClick={handleAdminClick}
                    className="group p-2 rounded-lg hover:bg-white/5 transition-colors"
                    title="Admin Access"
                  >
                    <Eye className='w-5 h-5 sm:w-6 sm:h-6 cursor-pointer'/>
                  </button>
                </div>
              </div>
              
              {/* Bottom safe area spacer */}
              <div className="h-4 flex-shrink-0" />
            </div>
          </div>
        </>
      )}

      <AdminLoginModal 
        isOpen={isAdminModalOpen} 
        onClose={handleCloseModal} 
      />
    </>
  );
};

export default Sidebar;